import React from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { getScoreBand } from '@/lib/scoring';

interface TestSession {
  id: string;
  date: string;
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
}

interface GapClosedPanelProps {
  first: TestSession;
  latest: TestSession;
}

const THRESHOLD = 70; // A category is "closed" when it reaches Solid or above

interface GapItem {
  id: string;
  category: string;
  firstScore: number;
  latestScore: number;
  wasBelowThreshold: boolean;
  isNowAboveThreshold: boolean;
  delta: number;
  color: string;
}

export default function GapClosedPanel({ first, latest }: GapClosedPanelProps) {
  const gaps: GapItem[] = [
    {
      id: 'gap-engine',
      category: 'Engine',
      firstScore: first.engineScore,
      latestScore: latest.engineScore,
      wasBelowThreshold: first.engineScore < THRESHOLD,
      isNowAboveThreshold: latest.engineScore >= THRESHOLD,
      delta: latest.engineScore - first.engineScore,
      color: '#EF4444',
    },
    {
      id: 'gap-ignition',
      category: 'Ignition',
      firstScore: first.ignitionScore,
      latestScore: latest.ignitionScore,
      wasBelowThreshold: first.ignitionScore < THRESHOLD,
      isNowAboveThreshold: latest.ignitionScore >= THRESHOLD,
      delta: latest.ignitionScore - first.ignitionScore,
      color: '#F59E0B',
    },
    {
      id: 'gap-bounce',
      category: 'Bounce',
      firstScore: first.bounceScore,
      latestScore: latest.bounceScore,
      wasBelowThreshold: first.bounceScore < THRESHOLD,
      isNowAboveThreshold: latest.bounceScore >= THRESHOLD,
      delta: latest.bounceScore - first.bounceScore,
      color: '#10B981',
    },
  ];

  const closedGaps = gaps.filter(g => g.wasBelowThreshold && g.isNowAboveThreshold);
  const openGaps = gaps.filter(g => g.wasBelowThreshold && !g.isNowAboveThreshold);
  const strongCategories = gaps.filter(g => !g.wasBelowThreshold);

  return (
    <div className="card p-5 h-full flex flex-col">
      <h3 className="section-header mb-1">Gap Analysis</h3>
      <p className="text-xs text-zinc-500 mb-4">Category gaps vs Solid threshold (70)</p>

      {closedGaps.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5">
            <CheckCircle2 size={11} /> Gaps Closed
          </p>
          <div className="space-y-2">
            {closedGaps.map(g => (
              <div key={g.id} className="p-3 rounded-xl bg-emerald-400/8 border border-emerald-400/20">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-zinc-200">{g.category}</span>
                  <span className="text-xs font-mono text-emerald-400 font-semibold">+{g.delta} pts</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="font-mono">{g.firstScore}</span>
                  <span>→</span>
                  <span className="font-mono font-semibold text-emerald-400">{g.latestScore}</span>
                  <span className="text-emerald-400">✓ Above threshold</span>
                </div>
                <div className="h-1 bg-zinc-800 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${g.latestScore}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {openGaps.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1.5">
            <AlertCircle size={11} /> Still Developing
          </p>
          <div className="space-y-2">
            {openGaps.map(g => {
              const band = getScoreBand(g.latestScore);
              return (
                <div key={g.id} className="p-3 rounded-xl bg-amber-400/5 border border-amber-400/15">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-zinc-200">{g.category}</span>
                    <span className="text-xs font-mono text-amber-400 font-semibold">
                      {g.delta > 0 ? '+' : ''}{g.delta} pts
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className="font-mono">{g.firstScore}</span>
                    <span>→</span>
                    <span className="font-mono font-semibold" style={{ color: band.color }}>{g.latestScore}</span>
                    <span className="text-zinc-600">Need {THRESHOLD - g.latestScore} more</span>
                  </div>
                  <div className="h-1 bg-zinc-800 rounded-full mt-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${g.latestScore}%`, backgroundColor: band.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {strongCategories.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <CheckCircle2 size={11} className="text-zinc-500" /> No Gap — Strong
          </p>
          <div className="space-y-2">
            {strongCategories.map(g => {
              const band = getScoreBand(g.latestScore);
              return (
                <div key={g.id} className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-300">{g.category}</span>
                    <span className="font-mono text-sm font-bold" style={{ color: band.color }}>{g.latestScore}</span>
                  </div>
                  <div className="h-1 bg-zinc-800 rounded-full mt-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${g.latestScore}%`, backgroundColor: band.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-zinc-800">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="font-mono font-bold text-emerald-400 text-xl">{closedGaps.length}</p>
            <p className="text-xs text-zinc-600">Closed</p>
          </div>
          <div>
            <p className="font-mono font-bold text-amber-400 text-xl">{openGaps.length}</p>
            <p className="text-xs text-zinc-600">Open</p>
          </div>
          <div>
            <p className="font-mono font-bold text-zinc-400 text-xl">{strongCategories.length}</p>
            <p className="text-xs text-zinc-600">Strong</p>
          </div>
        </div>
      </div>
    </div>
  );
}